// payout.service — guard izin pembayaran komisi. 3 LANGKAH: Finance/O/M AJUKAN → O/M SETUJUI
// (SoD pengaju ≠ penyetuju; trigger guard_payout_decide di DB) → Finance BAYAR.
// Efek uang (Beban 'Komisi' + Tunai ke Tutup Kasir) terjadi di langkah BAYAR, bukan approve.
import { currentUser } from '@core/session';
import { genNo, rp } from '@domain/format';
import { logAudit } from '@core/audit';
import { pushNotif } from '@core/notif';
import * as repo from './payout.repo';

const REQ_ROLES = ['Owner', 'Manager', 'Admin Finance'];

export const bolehAjukan = (): boolean => REQ_ROLES.includes(currentUser()?.role ?? '');
export const bolehSetujui = (): boolean => ['Owner', 'Manager'].includes(currentUser()?.role ?? '');

export interface AjuanRow { emp_nama: string; emp_role: string; outlet: string | null; amount: number }

/** Ajukan pembayaran massal — satu batch, tiap orang dapat nomor KOM sendiri (saling terhubung via batch). */
export async function ajukan(rows: AjuanRow[], periode: string, method: string, catatan: string): Promise<string> {
  if (!bolehAjukan()) throw new Error('Pengajuan komisi hanya oleh Finance/Manager/Owner.');
  if (!rows.length) throw new Error('Pilih minimal satu orang.');
  if (!/^\d{4}-\d{2}$/.test(periode)) throw new Error('Periode wajib bulan tertentu.');
  const batchNo = genNo('KOMB');
  await repo.insertBatch(rows.map((r) => ({
    no: genNo('KOM'), batch_no: batchNo, emp_nama: r.emp_nama, emp_role: r.emp_role,
    outlet: r.outlet, periode, amount: r.amount, method, catatan: catatan || null,
  })));
  const total = rows.reduce((a, r) => a + r.amount, 0);
  await logAudit({ action: 'KOMISI_REQUEST', module: 'payment', refId: batchNo, meta: { periode, method, orang: rows.length, total } });
  await pushNotif('MANAGER', null, `💸 Pengajuan komisi ${periode}: ${rows.length} orang · ${rp(total)} (${method}) — menunggu approval. Batch ${batchNo}`);
  return batchNo;
}

/** Langkah 2 — O/M putuskan batch. Approval ≠ pembayaran: efek uang baru terjadi di bayar(). */
export async function putuskan(batchNo: string, approve: boolean, note = ''): Promise<void> {
  if (!bolehSetujui()) throw new Error('Approval komisi hanya oleh Owner/Manager.');
  const rows = await repo.decideBatch(batchNo, approve, note.trim() || undefined); // trigger DB: SoD pengaju≠penyetuju
  const total = rows.reduce((a, r) => a + r.amount, 0);
  await logAudit({ action: approve ? 'KOMISI_APPROVE' : 'KOMISI_REJECT', module: 'payment', refId: batchNo, meta: { orang: rows.length, total, note: note.trim() || undefined } });
  await pushNotif('FINANCE', null, approve
    ? `✔ Komisi DISETUJUI — batch ${batchNo} (${rows.length} orang · ${rp(total)}). Silakan lakukan pembayaran.`
    : `✖ Komisi DITOLAK — batch ${batchNo} (${rows.length} orang · ${rp(total)}).${note.trim() ? ` Keterangan O/M: ${note.trim()}` : ''}`);
}

/** Langkah 3 — Finance BAYAR batch yang disetujui (isi via; tanggal = saat ini).
 *  DI SINILAH uang keluar: Beban 'Komisi' dibuat + (Tunai) ikut Tutup Kasir hari ini. */
export async function bayar(batchNo: string, via: string): Promise<repo.Payout[]> {
  if (!bolehAjukan()) throw new Error('Pembayaran komisi hanya oleh Finance/Manager/Owner.');
  const rows = await repo.payBatch(batchNo, via);
  for (const p of rows) {
    let expId: number | null = null;
    try { expId = await repo.insertKomisiExpense({ ...p, method: via }); }
    catch (e) { console.warn('[payout] BEBAN komisi GAGAL dibuat utk', p.no, '- susulkan manual di menu Beban:', e); continue; }
    try { await repo.linkExpense(p.id, expId); }
    catch (e) { console.warn('[payout] beban', expId, 'terbuat tapi tautan expense_id gagal utk', p.no, '- JANGAN buat beban manual:', e); }
  }
  const total = rows.reduce((a, r) => a + r.amount, 0);
  await logAudit({ action: 'KOMISI_PAID', module: 'payment', refId: batchNo, meta: { orang: rows.length, total, via } });
  await pushNotif('MANAGER', null, `💵 Komisi DIBAYAR — batch ${batchNo} (${rows.length} orang · ${rp(total)}) via ${via}.`);
  return rows;
}
